import { stringNotEmpty } from '@webapp/common/lib/utils';
import { CANTRATE_ID } from '@webapp/common/resources/constants';
import { AnswerType, QuestionType } from '@webapp/common/resources/survey';

import type { AnswersRequest, QuestionApi } from '@webapp/survey/src/api/queries-api';
import type { ResponseEventParams } from '@webapp/survey/src/entities/response-machine';
import type { GroupMachineActor } from '@webapp/survey/src/machine/group-machine';
import { excludeHiddenAnswers } from '@webapp/survey/src/machine/helpers/logic';
import type { QuestionActor, QuestionContext } from '@webapp/survey/src/machine/question-machine';

const checkGroups = (
    groups: Array<GroupMachineActor>,
    answerId: number,
    answerType: AnswerType,
): [number, boolean, boolean] => {
    let answeredCount = 0;
    let haveException = false;
    let haveCantRate = false;

    groups.forEach((groupActor) => {
        const group = groupActor.getSnapshot().context;
        const response = group.responses[answerId]?.getSnapshot().context;

        if (!response) return;

        const value = response?.value;
        const { exception, type } = group;

        if (value === CANTRATE_ID) {
            haveCantRate = true;
        }

        if (exception && value) {
            answeredCount = 1;
            haveException = true;

            return;
        }

        //checkbox with self
        // TODO radio with self failed here
        if (answerType === AnswerType.CHECKBOX && type === AnswerType.SELF) {
            if (stringNotEmpty(response?.extra as string)) {
                answeredCount += 1;
            }

            return;
        }

        switch (type) {
            case AnswerType.SELF:
                const text = response?.extra;
                if (value && text && stringNotEmpty(text as string)) {
                    answeredCount += 1;
                }
                break;
            case AnswerType.SELECT:
            case AnswerType.CHECKBOX:
            case AnswerType.TEXT:
            case AnswerType.RADIO:
                if (value) {
                    answeredCount += 1;
                }
                break;
        }
    });

    return [answeredCount, haveException, haveCantRate];
};

const processAnswersAndReturnIsAnswersValid = (question: QuestionContext, answerType: AnswerType): boolean => {
    const { answersActors, groupsActors, params, type } = question;
    const { maxLength, minLength, required, cantRate } = params;
    const answerWithText = answerType === AnswerType.TEXT || answerType === AnswerType.SELECT;
    const haveMaxLength = QuestionType.MATRIX_DROPDOWN_LIST !== type && typeof maxLength === 'number';
    const haveMinLength = QuestionType.MATRIX_DROPDOWN_LIST !== type && typeof minLength === 'number';
    let isAnyInvalid = false;
    excludeHiddenAnswers(answersActors).forEach((answerActor) => {
        const answer = answerActor.getSnapshot().context;
        const [answeredCount, haveException, haveCantRate] = checkGroups(groupsActors, answer.id, answerType);

        let answerValid: boolean = (() => {
            switch (type) {
                case QuestionType.MATRIX_DROPDOWN_LIST:
                    return answeredCount === groupsActors.length;
                default:
                    return answeredCount > 0;
            }
        })();
        if (answerWithText && required && !haveMaxLength && !haveMinLength) {
            answerValid = answerValid && answeredCount === groupsActors.length;
        }
        if (cantRate && haveCantRate) {
            answerValid = true;
        } else if (haveMinLength) {
            answerValid = answerValid && answeredCount >= minLength;
        }
        if (haveMaxLength) {
            answerValid = answerValid && answeredCount <= maxLength;
        }
        answerValid = haveException || answerValid;
        answerActor.send({ type: 'SET_ANSWERED_COUNT', value: answeredCount });
        answerActor.send({ type: 'SET_VALID', value: answerValid });

        if (!answerValid) {
            isAnyInvalid = true;
        }
    });
    return !isAnyInvalid;
};

export const validateWithSelf = (question: QuestionContext, answerType: AnswerType): boolean => {
    const { params, responseActor } = question;
    const { commentRequired, required } = params;

    if (!required) return true;

    const validAnswersInplace = processAnswersAndReturnIsAnswersValid(question, answerType);

    const validComment = commentRequired ? !!responseActor.getSnapshot().context.comment : true;

    if (commentRequired) {
        (responseActor._parent as QuestionActor).send({ type: 'SET_COMMENT_INVALID', value: !validComment });
    }

    return validAnswersInplace && validComment;
};

export abstract class MatrixApi implements QuestionApi {
    public change(_q: QuestionContext, _evt: ResponseEventParams) {}

    public changeExtra(actor: QuestionActor, value: PrimitiveValue): void {
        const { responseActor } = actor.getSnapshot().context;
        responseActor.send({ type: 'CHANGE_EXTRA', value });
    }

    public query(actor: QuestionActor): AnswersRequest {
        const { groupsActors, id, responseActor, answersActors } = actor.getSnapshot().context;
        const { comment } = responseActor.getSnapshot().context;
        const answerIds = excludeHiddenAnswers(answersActors).map((a) => a.getSnapshot().context.id.toString());
        return {
            question: id,
            data: groupsActors.flatMap((groupActor) => {
                const { id: group, responses } = groupActor.getSnapshot().context;
                return Object.entries(responses)
                    .filter(
                        ([answerId, responseActor]) =>
                            responseActor.getSnapshot().context.value && answerIds.includes(answerId.toString()),
                    )
                    .map(([answerId, responseActor]) => {
                        const { extra, value } = responseActor.getSnapshot().context;

                        if (value === CANTRATE_ID) {
                            return {
                                group,
                                answer: parseInt(answerId),
                                bool: true,
                                cantRate: true,
                            };
                        }

                        return {
                            group,
                            answer: parseInt(answerId),
                            bool: Boolean(value),
                            text: String(extra) || undefined,
                        };
                    });
            }),
            comment,
        };
    }

    public validate(question: QuestionContext): boolean {
        return validateWithSelf(question, AnswerType.CHECKBOX);
    }
}
